import React, { useState, useEffect } from "react";
import { Upload, Plus } from "lucide-react";
import { types } from "../lib/opportunityTypes";
import Select from "react-select";
import AddOpportunityModal from "./AddOpportunityModal";
import EditOpportunityModal from "./EditOpportunityModal";
import OpportunityCard from "./OpportunityCard";
import BulkUploadOpportunities from "./BulkUploadOpportunities";
import { toast } from "react-toastify";
import { getMergedData, updateStoredData } from "../lib/dataUtils";

const Opportunities = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [currentOpportunity, setCurrentOpportunity] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [students, setStudents] = useState([]);
  const [timeLogs, setTimeLogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProgram, setSelectedProgram] = useState("");
  const [selectedType, setSelectedType] = useState("");

  useEffect(() => {
    const mergedOpportunities = getMergedData("opportunities");
    const mergedStudents = getMergedData("students");
    const mergedTimeLogs = getMergedData("timeLogs");
    setOpportunities(mergedOpportunities);
    setStudents(mergedStudents);
    setTimeLogs(mergedTimeLogs);
  }, []);

  useEffect(() => {
    if (opportunities.length > 0) {
      updateStoredData("opportunities", opportunities);
    }
  }, [opportunities]);

  const handleAddOpportunity = (newOpportunity) => {
    setOpportunities((prevOpportunities) => [
      ...prevOpportunities,
      newOpportunity,
    ]);
    setIsAddModalOpen(false);
  };

  const handleEditOpportunity = (opportunityId) => {
    const opportunityToEdit = opportunities.find(
      (op) => op.id === opportunityId
    );
    setCurrentOpportunity(opportunityToEdit);
    setIsEditModalOpen(true);
  };

  const handleUpdateOpportunity = (updatedOpportunity) => {
    setOpportunities((prevOpportunities) =>
      prevOpportunities.map((opp) =>
        opp.id === updatedOpportunity.id ? updatedOpportunity : opp
      )
    );
    setIsEditModalOpen(false);
  };

  const handleDeleteOpportunity = (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this opportunity?"
    );
    if (confirmed) {
      const updatedOpportunities = opportunities.filter((op) => op.id !== id);
      setOpportunities(updatedOpportunities);
      toast.success("Opportunity deleted successfully!");
    }
  };

  const getNextId = () => {
    const maxId = opportunities.reduce((max, opp) => Math.max(max, opp.id), 0);
    return maxId + 1;
  };

  const handleBulkUpload = (uploadedOpportunities) => {
    let nextId = getNextId();
    const newOpportunities = uploadedOpportunities.map((op) => ({
      id: nextId++,
      hostName: op.hostName || "",
      hostPhoneNumber: op.hostPhoneNumber || "",
      streetAddress: op.streetAddress || "",
      city: op.city || "",
      stateProvince: op.stateProvince || "",
      postalCode: op.postalCode || "",
      requiredDress: op.requiredDress || "",
      schedule: op.schedule || {},
      opportunityType: op.opportunityType || "",
      students: op.students || [],
      siteVisit: op.siteVisit || false,
      dateOfVisit: op.dateOfVisit || null,
      visitBy: op.visitBy || null,
    }));

    setOpportunities([...opportunities, ...newOpportunities]);
    toast.success(
      `${newOpportunities.length} opportunities added successfully!`
    );
    setIsBulkUploadModalOpen(false);
  };

  const handleAddStudentToOpportunity = (opportunityId, studentId) => {
    const updatedOpportunities = opportunities.map((op) => {
      if (op.id === opportunityId) {
        return {
          ...op,
          students: [...new Set([...op.students, studentId])],
        };
      }
      return op;
    });
    setOpportunities(updatedOpportunities);
    updateStoredData("opportunities", updatedOpportunities);
    toast.success("Student added to opportunity successfully!");
  };

  const handleRemoveStudent = (opportunityId, studentId) => {
    setOpportunities(
      opportunities.map((opp) => {
        if (opp.id === opportunityId) {
          return {
            ...opp,
            students: opp.students.filter((id) => id !== studentId),
          };
        }
        return opp;
      })
    );
  };

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h2 className="text-3xl font-bold text-gray-800">Opportunities</h2>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-md flex items-center justify-center shadow-md transition duration-300 w-full sm:w-auto"
          >
            <Plus size={20} className="mr-2" />
            Add Opportunity
          </button>
          <button
            onClick={() => setIsBulkUploadModalOpen(true)}
            className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-md flex items-center justify-center shadow-md transition duration-300 w-full sm:w-auto"
          >
            <Upload size={20} className="mr-2" />
            Bulk Upload
          </button>
        </div>
      </div>

      <div className="mb-6 flex gap-4 flex-col md:flex-row">
        <div className="max-w-md flex-1">
          <input
            type="text"
            placeholder="Search opportunities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="w-full md:w-64">
          <Select
            isClearable
            options={types}
            onChange={(option) => setSelectedType(option ? option.value : "")}
            placeholder="Filter by Type"
            className="w-full"
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '42px',
                background: 'white',
                borderColor: '#E5E7EB',
                '&:hover': {
                  borderColor: '#9CA3AF'
                }
              }),
              placeholder: (base) => ({
                ...base,
                color: '#6B7280'
              })
            }}
          />
        </div>
        <div className="w-full md:w-64">
          <select
            value={selectedProgram}
            onChange={(e) => setSelectedProgram(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Programs</option>
            {["Biology", "Business Administration", "Chemical Engineering", "Chemistry", "Computer Science", "Electrical Engineering", "Mechanical Engineering", "Physics", "Psychology"].map((program) => (
              <option key={program} value={program}>
                {program}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {opportunities
          .filter(opportunity => {
            const matchesSearch = 
              opportunity.hostName.toLowerCase().includes(searchQuery.toLowerCase()) ||
              opportunity.opportunityType.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesType = !selectedType || (Array.isArray(opportunity.opportunityType) ? 
              opportunity.opportunityType.includes(selectedType) : 
              opportunity.opportunityType === selectedType);
            const matchesProgram = !selectedProgram || 
              opportunity.students.some(studentId => 
                students.find(s => s.studentId === studentId)?.program === selectedProgram
              );
            return matchesSearch && matchesProgram && matchesType;
          })
          .sort((a, b) => a.hostName.localeCompare(b.hostName))
          .map((opportunity) => (
            <OpportunityCard
              key={opportunity.id}
              opportunity={opportunity}
              onEdit={() => handleEditOpportunity(opportunity.id)}
              onDelete={() => handleDeleteOpportunity(opportunity.id)}
              onAddStudent={handleAddStudentToOpportunity}
              onRemoveStudent={handleRemoveStudent}
              students={students}
              timeLogs={timeLogs}
            />
          ))}
      </div>

      <AddOpportunityModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddOpportunity}
      />

      {currentOpportunity && (
        <EditOpportunityModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={handleUpdateOpportunity}
          opportunity={currentOpportunity}
        />
      )}

      <BulkUploadOpportunities
        isOpen={isBulkUploadModalOpen}
        onClose={() => setIsBulkUploadModalOpen(false)}
        onBulkUpload={handleBulkUpload}
      />
    </div>
  );
};

export default Opportunities;
